// "RETURNED" / "CANCELLED" stamp on an invoice/challan whose order has an
// approved refund in core_returns, or was cancelled. Its own layer ABOVE the content (zIndex), not
// inside the faint brand-watermark wrapper — inside it, the logo
// watermark and the line items painted over the stamp. On a printed
// invoice the layer is pinned per page like the watermark
// (.refund-stamp-layer in the global CSS); a challan copy is already
// exactly one page, so there it simply stays absolute.
export interface InvoiceRefund {
  // Sum of approved refund qty for the order; 0 when the return rows
  // predate core_returns.qty being filled in (treated as a full refund).
  qty: number;
  // Total units on the order (core_order_items), to tell full vs partial.
  orderQty: number;
  // Latest approved refund's updated_at — core_returns has no dedicated
  // approved_at, and approval is the last write to the row.
  refundedAt: string | null;
  // Latest approved refund's core_returns.reason, printed under the stamp.
  reason: string | null;
}

const REFUND_RED = '#B42318';

export function isPartialRefund(refund: InvoiceRefund): boolean {
  return refund.qty > 0 && refund.orderQty > 0 && refund.qty < refund.orderQty;
}

export function refundStatusLabel(refund: InvoiceRefund): string {
  return isPartialRefund(refund) ? `Partially Returned (${refund.qty} pcs)` : 'Returned';
}

// Cancelled wins over a refund — a cancelled order's invoice is void
// altogether, whatever was refunded along the way. The reason prints
// inside the stamp's border, under the label, as part of the stamp.
export function RefundWatermark({
  refund,
  cancelled,
  cancelReason,
}: {
  refund?: InvoiceRefund | null;
  cancelled?: boolean;
  /** core_orders.cancellation_reason */
  cancelReason?: string | null;
}) {
  if (!cancelled && !refund) return null;
  const label = cancelled ? 'CANCELLED' : 'RETURNED';
  const partial = !cancelled && !!refund && isPartialRefund(refund);
  const reason = (cancelled ? cancelReason : refund?.reason)?.trim();
  return (
    <div className="refund-stamp-layer absolute inset-0 overflow-hidden pointer-events-none flex items-center justify-center" style={{ zIndex: 20 }} aria-hidden="true">
      <div
        style={{
          transform: 'rotate(-28deg)',
          border: `4px solid ${REFUND_RED}`,
          borderRadius: '10px',
          padding: '8px 20px',
          color: REFUND_RED,
          opacity: 0.22,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          lineHeight: 1,
          letterSpacing: '0.08em',
          whiteSpace: 'nowrap',
        }}
      >
        {partial && <div style={{ fontSize: '26px', marginBottom: '4px' }}>PARTIALLY</div>}
        <div style={{ fontSize: '64px' }}>{label}</div>
        {reason && (
          <div
            style={{
              marginTop: '6px',
              maxWidth: '420px',
              marginLeft: 'auto',
              marginRight: 'auto',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {reason}
          </div>
        )}
      </div>
    </div>
  );
}
